<script lang="ts" setup>
import type { InflationReward } from '@solana/web3.js'
import type { StakeAccountWithState } from '~/store/stake-accounts'
import { JSTAKING_VALIDATOR_VOTE } from '~/config'
import { useCoinRateStore } from '~/store/coin-rate'
import { copy, formatAmount, formatMoney, lamportsToSol, shortenAddress } from '~/utils'

const { option, modelValue } = defineProps<{
  index: number
  option: StakeAccountWithState
  modelValue?: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const stakeAccountStore = useStakeAccountStore()

const loadingPubkeys = computed(() => stakeAccountStore.loadingPubkeys)
const loading = computed(() => loadingPubkeys.value.has(option.stakeAccount.pubkey.toBase58()))

const coinRateStore = useCoinRateStore()

const val = ref(false)

const rewards = computed(() => option.stakeAccount.rewards ?? [])

const solShow = computed(() => {
  return formatAmount(
    rewards.value?.reduce(
      (sum: number, current: InflationReward) => sum + lamportsToSol(Number(current.amount)),
      0,
    ) ?? 0,
  )
})

const totalRewardsSol = computed(() => {
  return formatAmount(Number(solShow.value) + lamportsToSol(option.stakeAccount.account.lamports))
})
const totalAmountUsd = computed(() => {
  return formatMoney(coinRateStore.solana?.price * Number(totalRewardsSol.value))
})

async function deactivate() {
  await stakeAccountStore.deactivate([option.stakeAccount.pubkey.toBase58()])
}

async function activate() {
  await stakeAccountStore.activate([option.stakeAccount.pubkey.toBase58()], JSTAKING_VALIDATOR_VOTE)
}

async function withdraw() {
  await stakeAccountStore.withdraw([{
    address: option.stakeAccount.pubkey.toBase58(),
    lamports: option.stakeAccount.account.lamports,
  }])
}

const stakeAccountPubkey = computed(() => shortenAddress(option.stakeAccount.pubkey.toBase58(), 5))

watch(val, (newValue) => {
  emit('update:modelValue', newValue)
})

watch(() => modelValue, (newValue) => {
  val.value = newValue
})
</script>

<template>
  <div class="unstake-widget-option">
    <div class="stake-item">
      <div class="stake-item-row">
        <div class="stake-item__number">
          #{{ index + 1 }}
        </div>
        <div class="fill-space" />
        <div class="stake-item__badge" :class="`badge-${option.state}`">
          {{ option.state }}
        </div>
        <template v-if=" option.state === 'active' || option.state === 'activating'">
          <q-btn :loading="loading" class="stake-action stake-deactivate" @click="deactivate">
            <i-app-activate-icon />
            <span>Deactivate</span>
          </q-btn>
        </template>
        <template v-else>
          <q-btn :loading="loading" :disabled="option.state === 'deactivating'" class="stake-action stake-activate" @click="activate">
            <i-app-activate-icon />
          </q-btn>
          <q-btn :loading="loading" :disabled="option.state === 'deactivating'" class="stake-action stake-withdraw" @click="withdraw">
            WITHDRAW
          </q-btn>
        </template>
      </div>
      <div class="stake-item-row">
        <span class="stake-item__label">My stake</span>
        <div class="fill-space" />
        <span class="stake-item__amount amount-currency">${{ totalAmountUsd }}</span>
        <span class="stake-item__amount amount-sol">{{ totalRewardsSol }}SOL</span>
      </div>
      <div class="stake-item-row">
        <span class="stake-item__label">Address</span>
        <div class="fill-space" />
        <span class="stake-item__address" @click="copy(option.stakeAccount.pubkey.toBase58())"><i-app-copy /> {{ stakeAccountPubkey }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.select-menu__item {
  border-bottom: 1px solid $gray-02;
}
.select-menu__item[active='true'] {
  .unstake-indicator {
    visibility: visible;
  }
}

.unstake-widget-option {
  width: 100%;
  padding: $space-lg $space-sm $space-lg $space-sm;
  display: flex;
  gap: $border-radius-md;

  @media (max-width: $breakpoint-sm) {
    width: 100%;
  }

  .unstake-indicator {
    color: $primary;
    visibility: hidden;
  }

  .unstake-info {
    width: 100%;

    &__title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: $space-md;
      border-bottom: 1px solid #2d2d2e;

      span {
        color: $black;
        font-weight: 500;
      }
    }

    &__details {
      padding-top: $space-md;
      display: flex;
      flex-direction: column;
      gap: $space-xxs;

      .detail-item {
        display: flex;
        align-items: center;

        &__label {
          text-transform: uppercase;
          font-weight: 500;
          color: $zinc-400;

          span {
            font-variant-numeric: slashed-zero;
            color: $zinc-50;
          }
        }

        &__value {
          margin: 0 $space-lg 0 auto;
          color: $zinc-400;
          font-variant-numeric: slashed-zero;
        }

        &__amount {
          width: 80px;
          text-align: right;
          display: flex;
          justify-content: flex-end;
          gap: $space-xxs;

          span {
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }
  }
}
.stake-item {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  width: 100%;
}
.stake-item-row {
  display: flex;
  gap: 8px;
  align-items: center;

  .stake-item__number {
    color: $black;
    font-size: 18px;
    font-weight: 600;
    line-height: 20px;
  }

  .stake-item__label,
  .stake-item__amount,
  .amount-currency {
    color: $black;
    font-size: 16px;
    font-weight: 500;
    line-height: normal;
    text-transform: capitalize;
  }

  .amount-currency {
    color: $gray-01;
    margin-right: 16px;
  }

  .stake-item__address {
    color: $gray-01;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }

  .stake-item__badge {
    display: flex;
    height: 36px;
    min-width: 32px;
    padding: 12px;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    text-transform: uppercase;
    font-size: 13px;
    font-weight: 800;
    line-height: 16px;

    &.badge-active {
      background: rgba(32, 179, 149, 0.2);
      color: $green;
    }

    &.badge-inactive {
      color: $inactive-color;
      background: $inactive-bg;
    }

    &.badge-activating {
      color: $orange;
      background-color: $orange-50;
    }

    &.badge-deactivating {
      color: $red;
      background-color: $red-50;
    }
  }

  .actions {
    display: flex;
    gap: 8px;
  }
  .stake-action {
    padding: 8px 12px;
    border-radius: 4px;
    border: none;

    &::before {
      display: none;
    }

    .q-btn__content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 13px;
      line-height: 20px;
      font-weight: 800;
    }

    &.stake-deactivate {
      background: $gray-03;
      color: $gray-01;

      svg {
        color: $inactive-color;
      }
    }

    &.stake-activate {
      background: $green;
      color: $base-white;

      svg {
        color: $base-white;
      }
    }

    &.stake-withdraw {
      color: $base-white;
      background: $blue;
    }
  }
}
</style>
